import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { toast } from '../components/ui/sonner';
import { ShieldCheck, Mail, Loader2, ArrowRight, KeyRound, Copy, Info } from 'lucide-react';

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [debugOtp, setDebugOtp] = useState('');
  const [expiresIn, setExpiresIn] = useState(0);
  const autoSentRef = useRef(false); // guards against React StrictMode double-mount

  const handleSendOTP = async (targetEmail = email, isAuto = false) => {
    if (!targetEmail) return;
    setSendingOTP(true);
    try {
      const response = await apiCall('/api/auth/otp/send', {
        method: 'POST',
        body: JSON.stringify({ email: targetEmail }),
      });
      if (response.debug_otp) setDebugOtp(response.debug_otp);
      if (typeof response.expires_in === 'number') setExpiresIn(response.expires_in);
      if (response.email_sent === false) {
        // Backend generated a code but the email provider rejected the send
        // (e.g., Brevo IP allowlist). Be honest with the user instead of a
        // misleading green "sent" toast.
        toast.error(
          response.message ||
          'We generated your code but the email could not be sent. Please contact support.',
          { duration: 8000 }
        );
      } else if (response.reused) {
        if (!isAuto) toast.info('Existing code still valid — check your email');
      } else {
        toast.success('Verification code sent — check your email');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send code');
    } finally {
      setSendingOTP(false);
    }
  };

  // Pre-fill email + auto-send once
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) setEmail(emailParam);
    if (emailParam && !autoSentRef.current) {
      autoSentRef.current = true;
      handleSendOTP(emailParam, true);
    }
  }, []);

  // Countdown
  useEffect(() => {
    if (expiresIn <= 0) return;
    const id = setInterval(() => setExpiresIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [expiresIn]);

  const fmtTime = (s) => {
    if (s <= 0) return 'expired';
    const m = Math.floor(s / 60);
    const ss = String(s % 60).padStart(2, '0');
    return `${m}:${ss}`;
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiCall('/api/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ email, otp }),
      });
      toast.success('Email verified! You can now sign in.');
      navigate(`/login?email=${encodeURIComponent(email)}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyOtp = () => {
    if (debugOtp) {
      navigator.clipboard?.writeText(debugOtp);
      setOtp(debugOtp);
      toast.success('Code applied');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 bg-cyan-500/10 border border-cyan-500/30 rounded-2xl flex items-center justify-center mb-4">
              <ShieldCheck className="w-7 h-7 text-cyan-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Verify your email</h2>
            <p className="text-sm text-slate-400 mt-1 text-center">Enter the 6-digit code we sent to your email to activate your account.</p>
          </div>

          {/* Dev-mode visible OTP banner */}
          {debugOtp && (
            <div className="mb-5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 px-4 py-3 flex items-start gap-3" data-testid="debug-otp-banner">
              <KeyRound className="w-5 h-5 text-cyan-300 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] uppercase tracking-wider font-bold text-cyan-300">Dev mode — your code</p>
                <p className="text-2xl font-mono font-bold text-white tracking-[0.4em] mt-0.5">{debugOtp}</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  {expiresIn > 0 ? `Expires in ${fmtTime(expiresIn)}` : 'Expired — request a new code'}
                </p>
              </div>
              <button
                onClick={copyOtp}
                className="self-center px-2.5 py-1.5 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-bold inline-flex items-center gap-1"
                data-testid="copy-debug-otp-btn"
              >
                <Copy className="w-3.5 h-3.5" /> Use
              </button>
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-300 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 pl-10 pr-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="you@example.com"
                  required
                  data-testid="verify-email-input"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => handleSendOTP()}
              disabled={sendingOTP || !email}
              className="w-full h-11 bg-slate-800 text-white text-sm font-semibold rounded-lg hover:bg-slate-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="send-otp-button"
            >
              {sendingOTP ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Resend Code'}
            </button>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-300 mb-2">6-digit Code</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                className="w-full h-14 px-4 bg-slate-950 border border-slate-800 rounded-lg text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="••••••"
                required
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                data-testid="verify-otp-input"
              />
              <p className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                <Info className="w-3 h-3" /> One active code per email; resending within 60s reuses the same code.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full h-12 bg-cyan-500 text-slate-950 font-bold rounded-lg hover:bg-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="verify-submit-button"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : <>Verify & Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-6">
            Already verified? <button onClick={() => navigate('/login')} className="text-cyan-400 hover:underline">Sign in</button>
          </p>
        </div>
      </div>
    </div>
  );
}
